/**
 * Antiforgery (CSRF) helper module
 *
 * Provides utilities to retrieve the antiforgery token from multiple sources
 * and to build standard headers for AJAX/fetch requests.
 */

import { CONFIG } from '../core/config.js';
import errorHandler from '../core/error-handler.js';

/**
 * Get antiforgery token from input field
 * @returns {string|null} Token value or null if not found
 */
function getTokenFromInput() {
    try {
        const input = document.querySelector(
            `input[name="${CONFIG.ANTIFORGERY.TOKEN_INPUT_NAME}"]`
        );
        return input && input.value ? input.value : null;
    } catch (error) {
        errorHandler.handleError(error, { context: 'getTokenFromInput' });
        return null;
    }
}

/**
 * Get antiforgery token from meta tag
 * @returns {string|null} Token value or null if not found
 */
function getTokenFromMeta() {
    try {
        const meta = document.querySelector(`meta[name="${CONFIG.ANTIFORGERY.TOKEN_META_NAME}"]`);
        return meta && meta.content ? meta.content : null;
    } catch (error) {
        errorHandler.handleError(error, { context: 'getTokenFromMeta' });
        return null;
    }
}

/**
 * Get antiforgery token from cookie
 * @param {string} [cookieName] - Cookie name (defaults to CONFIG.ANTIFORGERY.COOKIE_NAME)
 * @returns {string|null} Token value or null if not found
 */
function getTokenFromCookie(cookieName = CONFIG.ANTIFORGERY.COOKIE_NAME) {
    try {
        const name = `${cookieName}=`;
        const decodedCookie = decodeURIComponent(document.cookie || '');
        const parts = decodedCookie.split(';');
        for (let i = 0; i < parts.length; i++) {
            const c = parts[i].trim();
            if (c.indexOf(name) === 0) {
                return c.substring(name.length, c.length);
            }
        }
        return null;
    } catch (error) {
        errorHandler.handleError(error, { context: 'getTokenFromCookie' });
        return null;
    }
}

/**
 * Get antiforgery token from any available source
 * @returns {string|null} Token value or null if not found
 */
export function getToken() {
    try {
        return getTokenFromInput() || getTokenFromMeta() || getTokenFromCookie();
    } catch (error) {
        errorHandler.handleError(error, { context: 'getToken' });
        return null;
    }
}

/**
 * Get headers object with antiforgery token
 * @returns {Object} Headers object with token or empty object
 */
export function getHeaders() {
    try {
        const token = getToken();
        return token ? { [CONFIG.ANTIFORGERY.HEADER_NAME]: token } : {};
    } catch (error) {
        errorHandler.handleError(error, { context: 'getHeaders' });
        return {};
    }
}

/**
 * Serialize antiforgery token for Kendo Grid Data callbacks
 * @description Returns an object with the antiforgery token in the format
 * expected by Kendo Grid's .Data() method for Create/Update operations
 * @returns {Object} Object with __RequestVerificationToken property
 */
export function serialize() {
    try {
        const token = getToken();
        return {
            __RequestVerificationToken: token || ''
        };
    } catch (error) {
        errorHandler.handleError(error, { context: 'serialize' });
        return {
            __RequestVerificationToken: ''
        };
    }
}

/**
 * Wire jQuery AJAX defaults to include antiforgery token
 * @description Sets up global jQuery AJAX configuration to automatically
 * include antiforgery token in all AJAX requests
 */
export function wireJqueryAjaxDefaults() {
    try {
        if (!window.jQuery || !window.jQuery.ajaxSetup) {
            errorHandler.handleError(new Error('jQuery is not available for antiforgery setup'), {
                context: 'wireJqueryAjaxDefaults',
                type: 'warning'
            });
            return;
        }
        const headers = getHeaders();
        if (headers && Object.keys(headers).length > 0) {
            window.jQuery.ajaxSetup({ headers: headers });
        }
    } catch (error) {
        errorHandler.handleError(error, { context: 'wireJqueryAjaxDefaults' });
    }
}
